import React, { ElementType } from 'react'
import { config } from '@config/config'
import { ContactUsOption } from '@typesApp/Config'
import { AnalyticsElement } from '@typesApp/Analytics'
import { IconAnchor } from '@components/ui-css/IconAnchor/IconAnchor'
import { IconButtonLink } from '@components/ui-css/IconButton/IconButton'
import { useEnvironment } from '@hooks/useEnvironment'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { useWebchat } from '@hooks/useWebchat'
import { openNewTab } from '@utils/openNewTab'
import { injectDataAttributes } from '@utils/dataAttributes'
import { injectAnalytics } from '@utils/analyticsAttributes'
import styles from '../styles/Error.module.scss'

export const ContactUsPanel: React.FC = () => {
  const configOptions = config.getFeatures()?.contactUs
  if (!configOptions?.length) return null

  const componentMap: Record<ContactUsOption, ElementType> = {
    chat: ChatAction,
    email: EmailAction,
    faq: FAQAction,
    phone: CallUsAction,
  }

  return (
    <div className={styles.contactUs}>
      {configOptions.map((variant, index) => {
        const Action = componentMap[variant]
        return Action ? <Action key={variant} analyticsIndex={index + 1} /> : null
      })}
    </div>
  )
}

type ActionProps = {
  analyticsIndex: number
}

const CallUsAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { t } = useAppTranslation()

  return (
    <IconAnchor
      href={`tel:${t('ContactInfo.phone')}`}
      underline
      iconProps={{ library: 'base', name: 'phone' }}
      preLink={t('ErrorPage.footer.phone-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.phone')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.phone')}</span>
    </IconAnchor>
  )
}

const ChatAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { t } = useAppTranslation()
  const { toggleWebchat } = useWebchat()

  const onClick = () => {
    toggleWebchat()
  }

  return (
    <IconButtonLink
      onClick={onClick}
      underline
      iconProps={{ library: 'base', name: 'chat' }}
      preLink={t('ErrorPage.footer.chat-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.chat')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.chat')}</span>
    </IconButtonLink>
  )
}

const FAQAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { faqUrl } = useEnvironment()
  const { t } = useAppTranslation()

  const onClick = () => {
    faqUrl && openNewTab(faqUrl)
  }

  return (
    <IconButtonLink
      onClick={onClick}
      underline
      iconProps={{ library: 'base', name: 'faq' }}
      preLink={t('ErrorPage.footer.faq-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.faq')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.faq')}</span>
    </IconButtonLink>
  )
}

const EmailAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { t } = useAppTranslation()

  return (
    <IconAnchor
      href={`mailto:${t('email')}`}
      underline
      iconProps={{ library: 'base', name: 'email' }}
      preLink={t('ErrorPage.footer.email-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.email')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.email')}</span>
    </IconAnchor>
  )
}
